/**
 * Emotional category inference + descriptors for AI/SEO semantic layers.
 *
 * These functions read the same inputs the reveal already has (relationship,
 * template, animationTheme, message) and produce crawlable plain-text
 * descriptors. They never affect visuals — only the invisible semantic layer
 * consumes them.
 */

export type EmotionalCategory =
  | 'gratitude'
  | 'apology'
  | 'love'
  | 'family-appreciation'
  | 'birthday-celebration'
  | 'anniversary-celebration'
  | 'affirmation'
  | 'encouragement'
  | 'tribute'
  | 'general-celebration';

const CATEGORY_COPY: Record<
  EmotionalCategory,
  { label: string; summary: string; keywords: string[] }
> = {
  gratitude: {
    label: 'Gratitude Message',
    summary:
      'A heartfelt gratitude message expressing genuine thanks to someone who shaped the sender\u2019s life.',
    keywords: ['gratitude message', 'thank you', 'appreciation', 'heartfelt thanks', 'emotional gratitude'],
  },
  apology: {
    label: 'Apology Experience',
    summary:
      'A sincere apology experience \u2014 owning responsibility without excuses, offered while the recipient is still here to read it.',
    keywords: ['apology message', 'sincere apology', 'making amends', 'emotional repair', 'I am sorry'],
  },
  love: {
    label: 'Love Message',
    summary:
      'A direct love message \u2014 plain, unvarnished words for a partner, soulmate, or someone deeply loved.',
    keywords: ['love message', 'romantic message', 'I love you', 'heartfelt love', 'living love letter'],
  },
  'family-appreciation': {
    label: 'Family Appreciation',
    summary:
      'A family appreciation message honoring a parent, sibling, child, or grandparent with words that usually go unsaid.',
    keywords: [
      'family appreciation',
      'message to mom',
      'message to dad',
      'message to family',
      'honoring parents',
    ],
  },
  'birthday-celebration': {
    label: 'Birthday Celebration',
    summary:
      'A birthday celebration message that honors a year of someone\u2019s life with specific, personal recognition.',
    keywords: ['birthday message', 'birthday celebration', 'milestone birthday', 'heartfelt birthday wish'],
  },
  'anniversary-celebration': {
    label: 'Anniversary Celebration',
    summary:
      'An anniversary celebration honoring years of partnership, marriage, or enduring friendship in real words.',
    keywords: ['anniversary message', 'anniversary celebration', 'milestone anniversary', 'years together'],
  },
  affirmation: {
    label: 'Affirmation Intent',
    summary:
      'An affirmation \u2014 a clear, emotional statement of someone\u2019s worth, identity, or strength, meant to be re-read on hard days.',
    keywords: ['affirmation', 'positive affirmation', 'emotional wellness', 'self worth', 'encouragement'],
  },
  encouragement: {
    label: 'Encouragement Message',
    summary:
      'An encouragement message \u2014 a rallying, supportive voice sent to someone facing a difficult chapter.',
    keywords: ['encouragement', 'support message', 'rallying cry', 'you got this', 'emotional support'],
  },
  tribute: {
    label: 'Living Tribute',
    summary:
      'A living tribute \u2014 the words usually saved for a eulogy, delivered while the person is still here to hear them.',
    keywords: ['living tribute', 'tribute message', 'honor while alive', 'celebrate people while they are alive'],
  },
  'general-celebration': {
    label: 'Emotional Celebration',
    summary:
      'An emotional celebration of someone\u2019s life \u2014 a Living Announcement sent before words become too late.',
    keywords: ['emotional celebration', 'living announcement', 'heartfelt message', 'meaningful message'],
  },
};

const RELATIONSHIP_HINTS: Record<string, EmotionalCategory> = {
  mom: 'family-appreciation',
  mother: 'family-appreciation',
  dad: 'family-appreciation',
  father: 'family-appreciation',
  parent: 'family-appreciation',
  parents: 'family-appreciation',
  son: 'family-appreciation',
  daughter: 'family-appreciation',
  child: 'family-appreciation',
  sibling: 'family-appreciation',
  brother: 'family-appreciation',
  sister: 'family-appreciation',
  grandparent: 'family-appreciation',
  grandmother: 'family-appreciation',
  grandfather: 'family-appreciation',
  family: 'family-appreciation',
  partner: 'love',
  spouse: 'love',
  husband: 'love',
  wife: 'love',
  boyfriend: 'love',
  girlfriend: 'love',
  soulmate: 'love',
  friend: 'gratitude',
  bestfriend: 'gratitude',
  mentor: 'gratitude',
  teacher: 'gratitude',
  coach: 'gratitude',
  colleague: 'gratitude',
  self: 'affirmation',
};

const TEMPLATE_HINTS: Record<string, EmotionalCategory> = {
  birthday: 'birthday-celebration',
  anniversary: 'anniversary-celebration',
  apology: 'apology',
  gratitude: 'gratitude',
  thankyou: 'gratitude',
  love: 'love',
  family: 'family-appreciation',
  encouragement: 'encouragement',
  affirmation: 'affirmation',
  tribute: 'tribute',
  legacy: 'tribute',
  forever_in_my_heart: 'tribute',
};

const norm = (s?: string | null) =>
  (s ?? '').toLowerCase().replace(/[^a-z]/g, '');

function fromText(message?: string): EmotionalCategory | null {
  const m = (message ?? '').toLowerCase();
  if (!m) return null;
  if (/(i\s*am\s*sorry|i'?m\s*sorry|forgive me|my apology|apologi[sz]e)/.test(m)) return 'apology';
  if (/(thank you|grateful|gratitude|appreciate you|thanks for)/.test(m)) return 'gratitude';
  if (/(happy birthday|on your birthday|another year)/.test(m)) return 'birthday-celebration';
  if (/(anniversary|years together|our years)/.test(m)) return 'anniversary-celebration';
  if (/(i love you|love you|my love|always loved)/.test(m)) return 'love';
  if (/(you are enough|you are loved|you matter|you can do)/.test(m)) return 'affirmation';
  return null;
}

export interface SemanticContext {
  category: EmotionalCategory;
  label: string;
  summary: string;
  keywords: string[];
}

export function inferEmotionalCategory(args: {
  relationship?: string;
  template?: string;
  animationTheme?: string;
  message?: string;
}): SemanticContext {
  const fromTpl = TEMPLATE_HINTS[norm(args.template)];
  const fromRel = RELATIONSHIP_HINTS[norm(args.relationship)];
  const fromMsg = fromText(args.message);
  const category: EmotionalCategory =
    fromTpl ?? fromMsg ?? fromRel ?? 'general-celebration';
  return { category, ...CATEGORY_COPY[category] };
}

/** Stable plain-text emotional summary suitable for AI crawlers. */
export function buildEmotionalSummary(args: {
  ctx: SemanticContext;
  recipientName?: string;
  relationship?: string;
  senderLabel?: string;
}): string {
  const { ctx, recipientName, relationship, senderLabel } = args;
  const to = recipientName ? ` to ${recipientName}` : '';
  const rel = relationship ? ` (${relationship})` : '';
  const from = senderLabel ? ` from ${senderLabel}` : '';
  return `${ctx.label}${from}${to}${rel}. ${ctx.summary} Part of Say It NOW — celebrate people while they\u2019re alive.`;
}
